import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogIn, UserPlus, User } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';
import { trpc } from '@/providers/trpc';

/**
 * Top-right header — adapts to the auth state:
 *
 * - Logged in (any method)   → avatar/name pill → /account
 * - Not logged in, web       → two pills: "Войти" (/login) + "Регистрация"
 *                               (/login?signup=1). The Регистрация pill
 *                               collapses to icon-only on small screens to
 *                               keep clear of the language switcher.
 * - Inside a real Telegram   → single "Кабинет" pill (no signup needed in TG)
 *   Mini App
 */
export default function AccountButton() {
  const { isInTelegram } = useTelegram();
  const { t } = useTranslation();

  const meQuery = trpc.account.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const isAuthenticated = !!meQuery.data && !meQuery.isError;

  // ─── Logged in ─────────────────────────────────────────────
  if (isAuthenticated && meQuery.data) {
    const me = meQuery.data;
    const initial = (me.firstName ?? me.telegramUsername ?? me.email ?? '?')[0]!;
    return (
      <Link
        to="/account"
        aria-label={t('common.account')}
        className="fixed top-4 right-[5.5rem] z-[70] flex items-center gap-2 pl-1 pr-3 py-1 glass-pill rounded-full
                   text-[#00D4FF] text-xs font-medium transition-all duration-300 hover:scale-105 active:scale-95"
      >
        {me.photoUrl ? (
          <img
            src={me.photoUrl}
            alt=""
            className="w-6 h-6 rounded-full object-cover"
          />
        ) : (
          <span
            className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold uppercase"
            style={{
              background:
                'linear-gradient(135deg, rgba(0,212,255,0.25) 0%, rgba(0,212,255,0.08) 100%)',
              color: '#00D4FF',
            }}
          >
            {initial}
          </span>
        )}
        <span className="hidden sm:inline max-w-[120px] truncate">
          {me.firstName ?? me.telegramUsername ?? me.email ?? t('common.account')}
        </span>
        <User size={12} className="sm:hidden" />
      </Link>
    );
  }

  // ─── Inside Mini App ───────────────────────────────────────
  if (isInTelegram) {
    return (
      <Link
        to="/account"
        aria-label={t('common.account')}
        className="fixed top-4 right-[5.5rem] z-[70] flex items-center gap-1.5 px-3 py-1.5 glass-pill rounded-full
                   text-[#00D4FF] text-xs font-medium transition-all duration-300 hover:scale-105 active:scale-95"
      >
        <User size={12} />
        <span>{t('common.account')}</span>
      </Link>
    );
  }

  // ─── Web, anonymous — Sign in + Sign up pair ───────────────
  // Sign in: solid Telegram-blue (primary brand entry).
  // Sign up: glass-secondary (low-emphasis, but always visible).
  return (
    <div className="fixed top-4 right-[5.5rem] z-[70] flex items-center gap-2">
      <Link
        to="/login?signup=1"
        aria-label="Регистрация"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-pill
                   text-[#00D4FF] text-xs font-semibold transition-all duration-300
                   hover:scale-105 active:scale-95"
      >
        <UserPlus size={12} />
        <span className="hidden sm:inline">Регистрация</span>
      </Link>
      <Link
        to="/login"
        aria-label={t('auth.signIn')}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full
                   text-white text-xs font-semibold transition-all duration-300
                   hover:scale-105 active:scale-95 shadow-lg shadow-[rgba(0,212,255,0.35)]"
        style={{ background: 'linear-gradient(135deg, #00D4FF 0%, #00A8CC 100%)' }}
      >
        <LogIn size={12} />
        <span>{t('auth.signIn')}</span>
      </Link>
    </div>
  );
}
